import React, { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from '../utils/axios';
import { passwordResetRequestSchema } from '../utils/validationSchemas';
import { useFormValidation } from '../hooks/useFormValidation';
import { ErrorMessage } from '../components/ErrorMessage';

interface ForgotPasswordFormData {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: ''
  });

  const { errors, validateField, validateForm } = useFormValidation(passwordResetRequestSchema);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name as keyof ForgotPasswordFormData, value);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm(formData)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/members/request-password-reset', formData);

      if (response.data.success) {
        setSuccess(true);
        toast.success('Password reset instructions sent!');
      }
    } catch (error: any) {
      // Always show success to prevent email enumeration
      setSuccess(true);
      toast.success('If the email exists, a password reset link has been sent');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 py-8 px-4 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-[#C49A3C]/20 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-[#0D5128] p-3 rounded-2xl">
              <img src="/website-04.png" alt="Hammad Foundation" className="h-12 w-auto object-contain" />
            </div>
          </div>

          <div className="flex justify-center mb-4">
            <CheckCircle size={48} className="text-[#0D5128]" />
          </div>

          <h1 className="text-2xl font-bold text-[#0D5128] mb-3">Check Your Email</h1>
          <p className="text-sm text-[#0D5128]/70 mb-6">
            If an account exists for <strong>{formData.email}</strong>, you will receive a password reset link shortly.
          </p>

          <div className="bg-[#C49A3C]/10 border border-[#C49A3C]/30 rounded-lg p-4 mb-6 text-left">
            <p className="text-xs font-semibold text-[#0D5128] mb-2">Next Steps:</p>
            <ul className="list-disc list-inside text-xs text-[#0D5128]/80 space-y-1">
              <li>Check your inbox for the password reset email</li>
              <li>Click the reset link in the email</li>
              <li>Create a new password</li>
              <li>The link is valid for 1 hour</li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
            <p className="text-xs text-amber-800">
              <strong>Didn't receive the email?</strong> Check your spam folder or try again in a few minutes.
            </p>
          </div>

          <Link
            to="/member/login"
            className="inline-flex items-center gap-2 w-full justify-center bg-[#0D5128] hover:bg-[#0a3e1f] text-white py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl no-underline"
          >
            <ArrowLeft size={18} />
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 py-8 px-4 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-[#C49A3C]/20">
        <Link to="/member/login" className="inline-flex items-center gap-2 text-[#0D5128]/60 no-underline text-sm mb-6 hover:text-[#0D5128] transition-colors">
          <ArrowLeft size={18} />
          Back to Login
        </Link>

        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-[#0D5128] p-3 rounded-2xl">
              <img src="/website-04.png" alt="Hammad Foundation" className="h-12 w-auto object-contain" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-[#0D5128] mb-1">Forgot Password?</h1>
          <p className="text-sm text-[#0D5128]/60">
            Enter your email and we'll send you reset instructions
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[#0D5128] mb-2">
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${
                  errors.email
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-[#0D5128]'
                }`}
                placeholder="your@email.com"
              />
            </div>
            <ErrorMessage message={errors.email} />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0D5128] hover:bg-[#0a3e1f] text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl mt-2"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Mail className="h-5 w-5" />
                Send Reset Link
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center space-y-2">
          <Link to="/register" className="text-sm text-[#0D5128]/60 hover:text-[#0D5128] block transition-colors">
            Don't have an account? Register
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
